import { useState } from "react";
import { Wand2, Loader2, Star, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { MODES, MODE_META } from "../../lib/modes";

const API = "/api";
const POOLS = [["daily", "Warm Up"], ["challenge", "Challenge"]];

const Stars = ({ n }) => (
  <span className="inline-flex text-amber-500" aria-label={`${n} stars`}>{Array.from({ length: n }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-current" />)}</span>
);

// Preview line for one planned drill, expandable to its question titles
const PlanRow = ({ d, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <li className="rounded-lg border border-border bg-background/60" data-testid={`auto-build-plan-${i}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm">
        <Stars n={d.difficulty} />
        <span className="font-medium truncate">{d.title}</span>
        <span className="text-xs text-muted-foreground ml-auto whitespace-nowrap">{d.question_ids.length} questions</span>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && (
        <ul className="px-3 pb-2 text-xs text-muted-foreground space-y-0.5">
          <li className="italic">{d.description}</li>
          {d.questions.map((q) => <li key={q.question_id}>• {q.title} <span className="opacity-70">({q.difficulty}★ · {q.pool === "challenge" ? "challenge" : "warm-up"})</span></li>)}
        </ul>
      )}
    </li>
  );
};

// Super-admin: build drills from unscheduled questions not yet in a drill, grouped within 1 star. Preview -> Create.
export const AutoBuildDrillsDialog = ({ open, onOpenChange, defaultMode, onCreated }) => {
  const [opts, setOpts] = useState({ mode: defaultMode || "python", pools: ["daily", "challenge"], per_drill: 3, auto_rate: true });
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (patch) => { setOpts((o) => ({ ...o, ...patch })); setPlan(null); };
  const togglePool = (p) => set({ pools: opts.pools.includes(p) ? opts.pools.filter((x) => x !== p) : [...opts.pools, p] });
  const perDrillOk = Number.isInteger(opts.per_drill) && opts.per_drill >= 3 && opts.per_drill <= 20;
  const canRun = opts.pools.length > 0 && perDrillOk && !busy;

  const run = async (dryRun) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/drills/auto-build`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...opts, dry_run: dryRun }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || "Could not build drills"); return; }
      if (dryRun) {
        setPlan(data);
        if (!data.drills.length) toast.info("No drills to build - every eligible question is already in a drill, scheduled, or there are fewer than 3.");
      } else {
        toast.success(`Created ${data.created} drill${data.created === 1 ? "" : "s"}${data.rated ? ` · rated ${data.rated} question${data.rated === 1 ? "" : "s"}` : ""}`);
        onCreated?.(data);
        onOpenChange(false);
        setPlan(null);
      }
    } catch {
      toast.error("Could not build drills");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setPlan(null); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="auto-build-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wand2 className="w-5 h-5 text-purple-500" /> Auto-build drills</DialogTitle>
          <DialogDescription>
            Takes every approved question of the chosen mode that is not scheduled and not already in a drill, sorts it by stars and cuts it into drills whose questions are within 1 star of each other. Each drill is named like "Python ★★★ Drill 7" and rated by its hardest question.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium mb-2">Mode</p>
            <div className="flex flex-wrap gap-2" data-testid="auto-build-modes">
              {MODES.map((m) => {
                const Icon = MODE_META[m].icon;
                const on = opts.mode === m;
                return (
                  <button key={m} type="button" onClick={() => set({ mode: m })} aria-pressed={on}
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${on ? "bg-purple-600 text-white border-purple-600" : "bg-background text-muted-foreground border-border hover:text-foreground"}`}
                    data-testid={`auto-build-mode-${m}`}>
                    <Icon className="w-4 h-4" /> {MODE_META[m].label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium mb-2">Take questions from</p>
              <div className="space-y-2">
                {POOLS.map(([p, label]) => (
                  <label key={p} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={opts.pools.includes(p)} onCheckedChange={() => togglePool(p)} data-testid={`auto-build-pool-${p}`} /> {label} pool
                  </label>
                ))}
                <p className="text-xs text-muted-foreground">Scheduled (dated) questions are never used.</p>
              </div>
            </div>
            <div className="space-y-3">
              <label className="block text-sm font-medium">Questions per drill
                <Input type="number" min={3} max={20} value={opts.per_drill} onChange={(e) => set({ per_drill: parseInt(e.target.value, 10) || 0 })} className="mt-1 w-28" data-testid="auto-build-per-drill" />
                <span className="block text-xs text-muted-foreground font-normal mt-1">3–20. A short final drill is kept if it has at least 3 questions.</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={opts.auto_rate} onCheckedChange={(v) => set({ auto_rate: !!v })} data-testid="auto-build-auto-rate" /> Auto-rate unrated questions first
              </label>
            </div>
          </div>

          {plan && (
            <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-2" data-testid="auto-build-preview">
              <p className="text-sm font-semibold">
                Preview: {plan.drills.length} drill{plan.drills.length === 1 ? "" : "s"} from {plan.eligible} eligible question{plan.eligible === 1 ? "" : "s"}
              </p>
              <p className="text-xs text-muted-foreground" data-testid="auto-build-summary">
                Skipped {plan.excluded.scheduled} scheduled · {plan.excluded.in_drill} already in a drill
                {plan.to_rate ? ` · ${plan.to_rate} will be auto-rated` : ""}
                {plan.unratable.length ? ` · ${plan.unratable.length} can't be rated (${plan.unratable.slice(0, 3).join(", ")}${plan.unratable.length > 3 ? "…" : ""})` : ""}
                {plan.unused.length ? ` · ${plan.unused.length} left unused (${plan.unused.map((u) => u.title).slice(0, 3).join(", ")}${plan.unused.length > 3 ? "…" : ""})` : ""}
              </p>
              {plan.drills.length > 0 && (
                <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                  {plan.drills.map((d, i) => <PlanRow key={d.title} d={d} i={i} />)}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          {!plan ? (
            <Button onClick={() => run(true)} disabled={!canRun} className="gap-2" data-testid="auto-build-preview-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Preview
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => run(true)} disabled={!canRun} data-testid="auto-build-refresh-btn">Refresh preview</Button>
              <Button onClick={() => run(false)} disabled={!canRun || !plan.drills.length} className="gap-2 bg-purple-600 hover:bg-purple-700 text-white" data-testid="auto-build-create-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Create {plan.drills.length} drill{plan.drills.length === 1 ? "" : "s"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
